import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  ElementRef,
  Inject,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Observable, SubscriptionLike, of, switchMap, take, tap } from 'rxjs';
import { PlanogramStateService } from 'src/services/planogram-state.service';
import {
  UpdateDatasetModal,
  DatasetNote,
  adminSku,
  adminCooler,
  AdminBaseData,
} from 'src/modals/planogram';
import { Papa } from 'ngx-papaparse';
import { AdminUpdatePreviewComponent } from '../admin-update-preview/admin-update-preview.component';
import { shelfColumns } from 'src/helper/static';

interface Planogram {
  id: number;
  fileName: string;
  lastModified: string;
}

interface DialogData {
  data: any;
}

@Component({
  selector: 'app-admin-data-update',
  templateUrl: './admin-data-update.component.html',
  styleUrls: ['./admin-data-update.component.css'],
})
export class AdminDataUpdateComponent implements OnInit, AfterViewInit {
  @ViewChild('fileInput') fileInputRef!: ElementRef;
  updated_data_list: Planogram[] = [];
  displayedColumns: string[] = ['sNo', 'fileName', 'lastModified', 'action'];
  dataSource = new MatTableDataSource<Planogram>();
  @ViewChild(MatPaginator) paginator: MatPaginator;
  dataNotes: DatasetNote[];
  admin_udpate_data_subscription: SubscriptionLike;
  admin_update_data_observable: Observable<UpdateDatasetModal> =
    this.planogramState.admin_update_data;
  admin_update_data: UpdateDatasetModal;
  loader: Observable<boolean> = this.planogramState.loader;
  is_show_loader = false;
  admin_sku_data_subscription: SubscriptionLike;
  admin_sku_data_observable: Observable<adminSku[]> =
    this.planogramState.admin_sku_data;
  admin_sku_data: adminSku[];
  admin_cooler_data_subscription: SubscriptionLike;
  admin_cooler_data_observable: Observable<adminCooler[]> =
    this.planogramState.admin_cooler_data;
  admin_cooler_data: adminCooler[];
  admin_base_data_subscription: SubscriptionLike;
  admin_base_data_observable: Observable<AdminBaseData[]> =
    this.planogramState.admin_base_data;
  admin_base_data: AdminBaseData[];
  selectedFile: string = 'sku_details';
  dialogRef: MatDialogRef<AdminUpdatePreviewComponent> | null = null;
  shelfColumnToCheck = shelfColumns;

  constructor(
    private planogramState: PlanogramStateService,
    public dialog: MatDialog,
    private papa: Papa,
  ) {
    this.planogramState.getAdminUpdateData();
    this.planogramState.getAdminSkuData();
    this.planogramState.getAdminCoolerData();
    this.planogramState.getAdminBaseData({ type: 'guidelines' });
  }

  ngOnInit(): void {
    this.admin_udpate_data_subscription =
      this.admin_update_data_observable.subscribe(
        (data: UpdateDatasetModal) => {
          if (
            data &&
            data.dataset_details &&
            Array.isArray(data.dataset_details)
          ) {
            this.admin_update_data = data;
            const datasetDetails = data.dataset_details;
            this.updated_data_list = datasetDetails.map((detail, index) => ({
              id: index + 1,
              fileName: detail.file_name,
              dataName: detail.data_name,
              lastModified: detail.last_updated_on,
            }));
            const datasetNotes = data.dataset_notes;
            this.dataNotes = datasetNotes.map((detail, index) => ({
              id: index + 1,
              file_name: detail.file_name,
              description: detail.description,
              heading: detail.heading,
            }));
            this.dataSource = new MatTableDataSource(this.updated_data_list);
            this.dataSource.paginator = this.paginator;
          }
        },
        (error) => {
          console.error('Error in subscription', error);
        }
      );
    this.admin_sku_data_subscription = this.admin_sku_data_observable.subscribe(
      (data) => {
        if (data && data.length > 0) {
          this.admin_sku_data = data;
        }
      }
    );
    this.admin_cooler_data_subscription =
      this.admin_cooler_data_observable.subscribe((data) => {
        if (data && data.length > 0) {
          this.admin_cooler_data = data;
        }
      });
    this.admin_base_data_subscription =
      this.admin_base_data_observable.subscribe((data) => {
        this.admin_base_data = data.slice(0, 10);
      });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }


  ngOnDestroy(): void {
    this.admin_sku_data_subscription.unsubscribe();
    this.admin_base_data_subscription.unsubscribe();
    this.admin_cooler_data_subscription.unsubscribe();
    this.admin_udpate_data_subscription.unsubscribe();
  }

  splitSentences(sentence:string){
   return  sentence.split("<br>")
  }


  handleUploadClick(file_name: string): void {
    this.fileInputRef.nativeElement.click();
    this.selectedFile = file_name;
  }

  uploadCsv(event: any) {
    let file: File = event.target.files[0];
    const reader = new FileReader();
    const formData = new FormData();
    let selectedFile = this.selectedFile;
    let planogramState = this.planogramState;
    reader.onload = (e: any) => {
      const csvContent = e.target.result;
      const papaParser = this.papa;

      const parsedData = papaParser.parse(csvContent, {
        header: true,
        skipEmptyLines: true
      });

      let fileData = parsedData.data;
      const headers = parsedData.meta.fields || [];

      fileData = fileData.map((row: any) => {
        if (row['sales_value_abs']) {
          row['sales_value_abs'] = parseFloat(row['sales_value_abs']).toFixed(2);
        }

        this.shelfColumnToCheck.forEach(column => {
          if (row[column] === '0') {
            row[column] = '-'
          }
        });

        return row;
      });

      const csvString = papaParser.unparse(fileData, { header: true });

      const blob = new Blob([csvString], { type: 'text/csv' });
      formData.append('file', blob);
      if (selectedFile === 'sku_details') {
        planogramState.uploadAdminSku(formData);
      } else if (selectedFile === 'cooler_details') {
        planogramState.uploadAdminCooler(formData);
      } else {
        formData.append('type', selectedFile);
        planogramState.uploadAdminBaseData(formData);
      }
    };
    reader.readAsText(file);
  }

  handleDownloadClick(event: any, file_name: string): void {
    event.stopPropagation();

    if (file_name === 'sku_details' || file_name === 'cooler_details') {
      const data =
        file_name === 'sku_details'
          ? this.admin_sku_data
          : this.admin_cooler_data;
      const headers = this.getFilteredHeaders(data[0], file_name);
      const config = { columns: headers, quotes: false };
      this.downloadData(file_name, config, data);
    } else {
      this.planogramState
        .getAdminUpdatedBaseData({ type: file_name })
        .subscribe(
          (data: any) => {
            let fileData = data.data;
            // fileData = fileData.map((row: any) => {
            //   if (row['sales_value_abs']) {
            //     let value = parseFloat(row['sales_value_abs']).toFixed(2);
            //     row['sales_value_abs'] = value;
            //   }
            //   return row;
            // });
            // console.log('fileData', fileData);
            if (fileData && fileData.length > 0) {
              const headers = this.getFilteredHeaders(fileData[0], file_name);
              const config = { columns: headers, delimiter: ',', header: true };
              this.downloadData(file_name, config, fileData);
            } else {
              console.error('No data available');
            }
          },
          (error) => {
            console.error('Error fetching admin_base_data:', error);
          }
        );
    }
  }

  getFilteredHeaders(dataItem: any, file_name: string): string[] {
    const excludedHeaders =
      file_name === 'sku_details' ? ['id', 'status', 'sku_name'] : ['id'];
    return Object.keys(dataItem).filter(
      (header) => !excludedHeaders.includes(header)
    );
  }

  handlePreviewClick(event: any, file_name: string): void {
    event.stopPropagation();

    let dataToPreview$: Observable<any>;

    if (file_name === 'sku_details' || file_name === 'cooler_details') {
      dataToPreview$ = of(
        file_name === 'sku_details'
          ? this.admin_sku_data.slice(0, 10)
          : this.admin_cooler_data.slice(0, 10)
      );

      dataToPreview$.subscribe((dataToPreview) => {
        const dialogRef = this.dialog.open(AdminUpdatePreviewComponent, {
          height: '100%',
          maxHeight: '80vh',
          width: '80%',
          data: { adminData: dataToPreview },
        });
      });
    } else {
      this.planogramState
        .getAdminUpdatedBaseData({ type: file_name })
        .subscribe(
          (data: any) => {
            let fileData = data.data;
            if (fileData && fileData.length > 0) {
              this.dialog.open(AdminUpdatePreviewComponent, {
                height: '90%',
                maxHeight: '620px',
                width: '80%',
                data: { adminData: fileData.slice(0, 10) },
              });
            } else {
              console.error('No data available');
            }
          },
          (error) => {
            console.error('Error fetching admin_base_data:', error);
          }
        );
    }
  }

  downloadData(filename: string, config: any, data: any) {
    let result = this.papa.unparse(data, config);
    let blob = new Blob(['\ufeff' + result], {
      type: 'text/csv;charset=utf-8;',
    });
    let dwldLink = document.createElement('a');
    let url = URL.createObjectURL(blob);
    let isSafariBrowser =
      navigator.userAgent.indexOf('Safari') != -1 &&
      navigator.userAgent.indexOf('Chrome') == -1;
    if (isSafariBrowser) {
      dwldLink.setAttribute('target', '_blank');
    }
    dwldLink.setAttribute('href', url);
    dwldLink.setAttribute('download', filename + '.csv');
    dwldLink.style.visibility = 'hidden';
    document.body.appendChild(dwldLink);
    dwldLink.click();
    document.body.removeChild(dwldLink);
  }
}
